"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardList,
  FaEdit,
  FaExclamationTriangle,
  FaFileAlt,
  FaFilter,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaTrash,
  FaUserTie,
} from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import ManagementHeader from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import { formatProfileName } from "../../../utils/profileName";

const EMPTY_FORM = {
  title: "",
  category: "Conduct",
  severity: "Informal",
  status: "Open",
  incidentDate: "",
  reviewDate: "",
  description: "",
  actionTaken: "",
  managerNotes: "",
  evidenceReference: "",
};

const CATEGORY_OPTIONS = [
  "Conduct",
  "Attendance",
  "Performance",
  "Policy breach",
  "Safeguarding",
  "Patient safety",
  "Other",
];

const SEVERITY_OPTIONS = [
  "Informal",
  "First written warning",
  "Final written warning",
  "Investigation",
  "Suspension",
];

const STATUS_OPTIONS = ["Open", "Under review", "Active warning", "Resolved", "Expired"];

function formatDate(value, withTime = false) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function caseReference(record) {
  return `DISC-${String(record?.id || "").replace(/-/g, "").slice(0, 8).toUpperCase() || "LEGACY"}`;
}

function statusClass(status) {
  const key = String(status || "Open").toLowerCase().replace(/\s+/g, "-");
  return `pguh-disciplinary-badge pguh-disciplinary-badge--${key}`;
}

function severityClass(severity) {
  const key = String(severity || "Informal").toLowerCase().replace(/\s+/g, "-");
  return `pguh-disciplinary-badge pguh-disciplinary-severity--${key}`;
}

export default function DisciplinariesPage() {
  const { isEnabled } = useFeatureFlags();
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [disciplinaries, setDisciplinaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    void loadPage();
  }, [profileId]);

  const loadPage = async () => {
    setLoading(true);
    setError("");

    const [profileResult, disciplinaryResult] = await Promise.all([
      supabase
        .from("profiles")
        .select("id, full_name, name_prefix, email, position, job_role, roblox_name, discord_name")
        .eq("id", profileId)
        .maybeSingle(),
      supabase
        .from("disciplinaries")
        .select("*")
        .eq("profile_id", profileId)
        .order("created_at", { ascending: false }),
    ]);

    if (profileResult.error) {
      console.error(profileResult.error);
      setError("The staff profile could not be loaded.");
    } else {
      setProfile(profileResult.data || null);
    }

    if (disciplinaryResult.error) {
      console.error(disciplinaryResult.error);
      setError((previous) => previous || "Disciplinary records could not be loaded.");
    } else {
      setDisciplinaries(disciplinaryResult.data || []);
    }

    setLoading(false);
  };

  const stats = useMemo(() => {
    const total = disciplinaries.length;
    const active = disciplinaries.filter((d) =>
      ["Open", "Under review", "Active warning"].includes(d.status || "Open"),
    ).length;
    const activeWarnings = disciplinaries.filter((d) => (d.status || "Open") === "Active warning").length;
    const resolved = disciplinaries.filter((d) => ["Resolved", "Expired"].includes(d.status)).length;
    return { total, active, activeWarnings, resolved };
  }, [disciplinaries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return disciplinaries.filter((record) => {
      const recordStatus = record.status || "Open";
      if (statusFilter !== "All" && recordStatus !== statusFilter) return false;
      if (!q) return true;
      return [
        record.title,
        record.description,
        record.category,
        record.severity,
        record.status,
        record.action_taken,
        record.manager_notes,
        record.evidence_reference,
        record.issued_by_name,
        caseReference(record),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [disciplinaries, search, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, incidentDate: new Date().toISOString().slice(0, 10) });
    setError("");
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setForm({
      title: record.title || "",
      category: record.category || "Conduct",
      severity: record.severity || "Informal",
      status: record.status || "Open",
      incidentDate: record.incident_date ? String(record.incident_date).slice(0, 10) : "",
      reviewDate: record.review_date ? String(record.review_date).slice(0, 10) : "",
      description: record.description || "",
      actionTaken: record.action_taken || "",
      managerNotes: record.manager_notes || "",
      evidenceReference: record.evidence_reference || "",
    });
    setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setError("");
  };

  const saveDisciplinary = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError("Case title and incident details are required.");
      return;
    }

    setSaving(true);
    setError("");

    const { data: authData } = await supabase.auth.getUser();
    const currentUser = authData?.user;
    let issuerName = currentUser?.email || "Management";

    if (currentUser?.id) {
      const { data: issuerProfile } = await supabase
        .from("profiles")
        .select("full_name, name_prefix")
        .eq("id", currentUser.id)
        .maybeSingle();
      issuerName = formatProfileName(issuerProfile, issuerName);
    }

    const payload = {
      profile_id: profileId,
      title: form.title.trim(),
      category: form.category,
      severity: form.severity,
      status: form.status,
      incident_date: form.incidentDate || null,
      review_date: form.reviewDate || null,
      description: form.description.trim(),
      action_taken: form.actionTaken.trim() || null,
      manager_notes: form.managerNotes.trim() || null,
      evidence_reference: form.evidenceReference.trim() || null,
      updated_at: new Date().toISOString(),
      ...(editing
        ? {}
        : {
            issued_by: currentUser?.id || null,
            issued_by_name: issuerName,
          }),
    };

    const query = editing
      ? supabase.from("disciplinaries").update(payload).eq("id", editing.id)
      : supabase.from("disciplinaries").insert(payload);

    const { error: saveError } = await query;
    if (saveError) {
      console.error(saveError);
      setError(saveError.message || "The disciplinary record could not be saved.");
      setSaving(false);
      return;
    }

    setSaving(false);
    closeModal();
    await loadPage();
  };

  const deleteDisciplinary = async (record) => {
    const confirmed = window.confirm(
      `Delete ${caseReference(record)}?\n\nThis permanently removes the disciplinary record and cannot be undone.`,
    );
    if (!confirmed) return;

    const { error: deleteError } = await supabase
      .from("disciplinaries")
      .delete()
      .eq("id", record.id);

    if (deleteError) {
      console.error(deleteError);
      alert("The disciplinary record could not be deleted.");
      return;
    }

    await loadPage();
  };

  const profileInitials = String(formatProfileName(profile, profile?.email || "?"))
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div className="flex flex-col min-h-screen">
      <ManagementHeader />

      <main className="nhsuk-width-container pguh-disciplinary-page">
        <button type="button" className="pguh-disciplinary-back" onClick={() => navigate("/intranet/management/profiles")}> 
          <FaArrowLeft aria-hidden="true" /> Back to profiles
        </button>

        <div className="pguh-disciplinary-heading-row">
          <div>
            <span className="pguh-disciplinary-eyebrow">People · Employee relations</span>
            <h1 className="nhsuk-heading-xl pguh-disciplinary-title">Disciplinary record</h1>
            <p className="nhsuk-body-l pguh-disciplinary-intro">
              Record, review and manage formal and informal disciplinary actions for this profile.
            </p>
          </div>
          <button type="button" className="nhsuk-button pguh-disciplinary-create" onClick={openCreate} disabled={!isEnabled("feature.management_disciplinaries")}>
            <FaPlus aria-hidden="true" /> New disciplinary
          </button>
        </div>

        <section className="pguh-disciplinary-person-card" aria-label="Profile">
          <div className="pguh-disciplinary-avatar" aria-hidden="true">{profileInitials || "?"}</div>
          <div className="pguh-disciplinary-person-main">
            <span className="pguh-disciplinary-person-label">Record for</span>
            <h2>{loading ? "Loading profile…" : formatProfileName(profile, "Unknown profile")}</h2>
            <div className="pguh-disciplinary-person-meta">
              {profile?.job_role && <span>{profile.job_role}</span>}
              {profile?.position && <span>{profile.position}</span>}
              {profile?.email && <span>{profile.email}</span>}
            </div>
          </div>
          {(profile?.roblox_name || profile?.discord_name) && (
            <div className="pguh-disciplinary-person-accounts">
              {profile?.roblox_name && <span><strong>Roblox</strong>{profile.roblox_name}</span>}
              {profile?.discord_name && <span><strong>Discord</strong>{profile.discord_name}</span>}
            </div>
          )}
        </section>

        {error && !modalOpen && (
          <div className="nhsuk-error-summary" role="alert">
            <h2 className="nhsuk-error-summary__title">There is a problem</h2>
            <div className="nhsuk-error-summary__body"><p>{error}</p></div>
          </div>
        )}

        <div className="pguh-disciplinary-stats">
          <div className="pguh-disciplinary-stat"><FaClipboardList /><div><strong>{stats.total}</strong><span>Total records</span></div></div>
          <div className="pguh-disciplinary-stat"><FaExclamationTriangle /><div><strong>{stats.active}</strong><span>Open / active</span></div></div>
          <div className="pguh-disciplinary-stat"><FaShieldAlt /><div><strong>{stats.activeWarnings}</strong><span>Active warnings</span></div></div>
          <div className="pguh-disciplinary-stat"><FaCheckCircle /><div><strong>{stats.resolved}</strong><span>Resolved / expired</span></div></div>
        </div>

        <section className="pguh-disciplinary-workspace">
          <div className="pguh-disciplinary-workspace-heading">
            <div>
              <h2 className="nhsuk-heading-m">Case history</h2>
              <p>Structured disciplinary records are shown newest first.</p>
            </div>
            <span className="pguh-disciplinary-result-count">{filtered.length} shown</span>
          </div>

          <div className="pguh-disciplinary-toolbar">
            <label className="pguh-disciplinary-search">
              <span className="nhsuk-u-visually-hidden">Search disciplinary records</span>
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search cases, details, manager or reference"
              />
            </label>
            <label className="pguh-disciplinary-filter">
              <FaFilter aria-hidden="true" />
              <span className="nhsuk-u-visually-hidden">Filter by status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option>All</option>
                {STATUS_OPTIONS.map((status) => <option key={status}>{status}</option>)}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="pguh-disciplinary-empty"><p>Loading disciplinary history…</p></div>
          ) : filtered.length === 0 ? (
            <div className="pguh-disciplinary-empty">
              <FaFileAlt aria-hidden="true" />
              <h3>{disciplinaries.length ? "No records match these filters" : "No disciplinary records"}</h3>
              <p>{disciplinaries.length ? "Change your search or status filter." : "This profile does not currently have a disciplinary history."}</p>
              {!disciplinaries.length && <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={openCreate} disabled={!isEnabled("feature.management_disciplinaries")}>Create first record</button>}
            </div>
          ) : (
            <div className="pguh-disciplinary-cases">
              {filtered.map((record) => (
                <article className="pguh-disciplinary-case" key={record.id}>
                  <div className="pguh-disciplinary-case-accent" aria-hidden="true" />
                  <div className="pguh-disciplinary-case-body">
                    <div className="pguh-disciplinary-case-header">
                      <div>
                        <div className="pguh-disciplinary-case-ref">{caseReference(record)}</div>
                        <h3>{record.title || record.description || "Legacy disciplinary record"}</h3>
                      </div>
                      <div className="pguh-disciplinary-case-badges">
                        <span className={severityClass(record.severity)}>{record.severity || "Legacy"}</span>
                        <span className={statusClass(record.status)}>{record.status || "Open"}</span>
                      </div>
                    </div>

                    <div className="pguh-disciplinary-case-meta">
                      <span><FaCalendarAlt /> <strong>Incident</strong> {formatDate(record.incident_date || record.created_at)}</span>
                      <span><FaClipboardList /> <strong>Category</strong> {record.category || "Not recorded"}</span>
                      <span><FaUserTie /> <strong>Issued by</strong> {record.issued_by_name || "Legacy record"}</span>
                      {record.review_date && <span><FaCalendarAlt /> <strong>Review</strong> {formatDate(record.review_date)}</span>}
                    </div>

                    <div className="pguh-disciplinary-section">
                      <h4>Incident / reason</h4>
                      <p>{record.description || "No description was recorded."}</p>
                    </div>

                    {(record.action_taken || record.manager_notes || record.evidence_reference) && (
                      <div className="pguh-disciplinary-detail-grid">
                        {record.action_taken && <div><strong>Action / outcome</strong><p>{record.action_taken}</p></div>}
                        {record.evidence_reference && <div><strong>Evidence / reference</strong><p>{record.evidence_reference}</p></div>}
                        {record.manager_notes && <div className="pguh-disciplinary-detail-wide"><strong>Management notes</strong><p>{record.manager_notes}</p></div>}
                      </div>
                    )}

                    <div className="pguh-disciplinary-case-footer">
                      <span>Created {formatDate(record.created_at, true)}{record.updated_at && record.updated_at !== record.created_at ? ` · Updated ${formatDate(record.updated_at, true)}` : ""}</span>
                      <div className="pguh-disciplinary-case-actions">
                        <button type="button" className="pguh-disciplinary-action" onClick={() => openEdit(record)}><FaEdit /> Edit</button>
                        <button type="button" className="pguh-disciplinary-action pguh-disciplinary-action--danger" onClick={() => deleteDisciplinary(record)}><FaTrash /> Delete</button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />

      {modalOpen && (
        <div className="pguh-disciplinary-modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && closeModal()}>
          <form className="pguh-disciplinary-modal" onSubmit={saveDisciplinary} role="dialog" aria-modal="true" aria-labelledby="disciplinary-modal-title">
            <div className="pguh-disciplinary-modal-header">
              <div>
                <span className="pguh-disciplinary-eyebrow">Employee relations</span>
                <h2 id="disciplinary-modal-title">{editing ? "Edit disciplinary record" : "Create disciplinary record"}</h2>
                <p>{formatProfileName(profile, profile?.email || "Selected profile")}</p>
              </div>
              <button type="button" className="pguh-disciplinary-modal-close" onClick={closeModal} aria-label="Close"><FaTimes /></button>
            </div>

            <div className="pguh-disciplinary-modal-body">
              {error && <div className="nhsuk-error-summary" role="alert"><h3 className="nhsuk-error-summary__title">Check the record</h3><p>{error}</p></div>}

              <div className="pguh-disciplinary-form-grid">
                <label className="pguh-disciplinary-field pguh-disciplinary-field--wide">
                  <span>Case title <b>*</b></span>
                  <input className="nhsuk-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Repeated failure to follow medicines policy" required />
                </label>

                <label className="pguh-disciplinary-field"><span>Category</span><select className="nhsuk-select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>{CATEGORY_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></label>
                <label className="pguh-disciplinary-field"><span>Level / severity</span><select className="nhsuk-select" value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>{SEVERITY_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></label>
                <label className="pguh-disciplinary-field"><span>Status</span><select className="nhsuk-select" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>{STATUS_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select></label>
                <label className="pguh-disciplinary-field"><span>Incident date</span><input className="nhsuk-input" type="date" value={form.incidentDate} onChange={(e) => setForm((f) => ({ ...f, incidentDate: e.target.value }))} /></label>
                <label className="pguh-disciplinary-field"><span>Review / expiry date</span><input className="nhsuk-input" type="date" value={form.reviewDate} onChange={(e) => setForm((f) => ({ ...f, reviewDate: e.target.value }))} /></label>

                <label className="pguh-disciplinary-field pguh-disciplinary-field--wide">
                  <span>Incident details / reason <b>*</b></span>
                  <textarea className="nhsuk-textarea" rows="5" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Record the factual reason for this disciplinary action." required />
                  <small>Keep this factual and relevant to the case.</small>
                </label>

                <label className="pguh-disciplinary-field pguh-disciplinary-field--wide"><span>Action taken / outcome</span><textarea className="nhsuk-textarea" rows="3" value={form.actionTaken} onChange={(e) => setForm((f) => ({ ...f, actionTaken: e.target.value }))} placeholder="e.g. First written warning issued; training to be completed before next shift." /></label>
                <label className="pguh-disciplinary-field pguh-disciplinary-field--wide"><span>Evidence / reference</span><input className="nhsuk-input" value={form.evidenceReference} onChange={(e) => setForm((f) => ({ ...f, evidenceReference: e.target.value }))} placeholder="DATIX reference, meeting note, document URL or other evidence reference" /></label>
                <label className="pguh-disciplinary-field pguh-disciplinary-field--wide"><span>Management notes</span><textarea className="nhsuk-textarea" rows="3" value={form.managerNotes} onChange={(e) => setForm((f) => ({ ...f, managerNotes: e.target.value }))} placeholder="Internal management notes, follow-up requirements or review information." /></label>
              </div>
            </div>

            <div className="pguh-disciplinary-modal-footer">
              <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={closeModal} disabled={saving}>Cancel</button>
              <button type="submit" className="nhsuk-button" disabled={saving}>{saving ? "Saving…" : editing ? "Save changes" : "Create disciplinary"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
